import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Sparkles, TrendingUp, ArrowRight, Calendar, ArrowDownToLine, Share2, Check, Copy } from "lucide-react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import ScoreCircle from "@/components/ScoreCircle";
import { useAuth } from "@/contexts/AuthContext";
import { Toaster, toast } from "sonner";

const SUBSCORE_LABELS = {
    clarity: "Clarity",
    credibility: "Credibility",
    consistency: "Consistency",
    attractiveness: "Attractiveness",
    visibility: "Visibility",
    professionalism: "Professionalism",
};

function colorFor(v) {
    if (v <= 4) return "#ef4444";
    if (v <= 7) return "#f97316";
    return "#22c55e";
}

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [shareUrl, setShareUrl] = useState(null);
    const [shareCopied, setShareCopied] = useState(false);
    const reportRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/reports/latest");
                setReport(data);
                if (data?.shareToken) {
                    setShareUrl(`${window.location.origin}/r/${data.shareToken}`);
                }
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const toggleShare = async () => {
        if (!report) return;
        try {
            if (shareUrl) {
                await api.delete(`/reports/${report.id}/share`);
                setShareUrl(null);
                toast.success("Share link revoked");
            } else {
                const { data } = await api.post(`/reports/${report.id}/share`);
                const url = `${window.location.origin}${data.url}`;
                setShareUrl(url);
                await navigator.clipboard.writeText(url);
                setShareCopied(true);
                setTimeout(() => setShareCopied(false), 2000);
                toast.success("Public link copied");
            }
        } catch {
            toast.error("Share failed");
        }
    };

    const copyShare = async () => {
        if (!shareUrl) return;
        await navigator.clipboard.writeText(shareUrl);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 1500);
    };

    const exportPDF = async () => {
        if (!reportRef.current) return;
        setExporting(true);
        try {
            const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
                import("html2canvas"),
                import("jspdf"),
            ]);
            const node = reportRef.current;
            const canvas = await html2canvas(node, {
                scale: 2,
                backgroundColor: "#ffffff",
                useCORS: true,
                logging: false,
            });
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "mm", "a4");
            const pageWidth = 210;
            const pageHeight = 297;
            const imgWidth = pageWidth - 20;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let position = 10;
            let heightLeft = imgHeight;
            pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
            heightLeft -= pageHeight - 20;
            while (heightLeft > 0) {
                position = heightLeft - imgHeight + 10;
                pdf.addPage();
                pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
                heightLeft -= pageHeight - 20;
            }
            pdf.save(`MirrorDigital-aura-report-${Date.now()}.pdf`);
            toast.success("PDF exported");
        } catch (err) {
            console.error(err);
            toast.error("PDF export failed");
        } finally {
            setExporting(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="cyber-spinner" />
                </div>
            </Layout>
        );
    }

    if (!report) {
        return (
            <Layout>
                <Toaster theme="light" position="top-center" />
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass p-12 text-center"
                    data-testid="empty-dashboard"
                >
                    <Sparkles className="w-12 h-12 text-aura-violet mx-auto mb-5" />
                    <h1 className="font-syne text-3xl font-bold mb-2 text-aura-text">Welcome, {user?.name}</h1>
                    <p className="text-aura-body mb-6">Your AURA hasn't been computed yet. Run your first identity scan.</p>
                    <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={() => navigate("/onboarding")}
                        className="px-6 py-3 rounded-lg bg-gradient-to-r from-aura-violet to-aura-glow text-white font-dm-sans font-medium glow-violet-soft btn-ripple"
                        data-testid="dashboard-start-btn"
                    >
                        Start Identity Scan →
                    </motion.button>
                </motion.div>
            </Layout>
        );
    }

    const out = report.output || {};
    const subscores = out.subscores || {};
    const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };
    const item = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } };

    return (
        <Layout>
            <Toaster theme="light" position="top-center" />
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                <div>
                    <div className="font-jetbrains-mono text-xs tracking-[0.3em] text-aura-violet mb-2" data-testid="dashboard-breadcrumb">
                        // DASHBOARD / IDENTITY SIGNAL
                    </div>
                    <h1 className="font-syne text-4xl sm:text-5xl font-bold text-aura-text">
                        Hi {user?.name?.split(" ")[0]}, your <span className="text-grad-violet">aura</span> is live.
                    </h1>
                </div>
                <div className="flex flex-wrap gap-2">
                    <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={toggleShare}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium btn-ripple transition ${
                            shareUrl
                                ? "bg-aura-violet/10 border border-aura-violet/30 text-aura-violet"
                                : "border border-aura-violet/25 bg-white text-aura-text hover:bg-aura-violet/5"
                        }`}
                        data-testid="share-report-btn"
                    >
                        <Share2 className="w-4 h-4" />
                        {shareUrl ? "Public" : "Share"}
                    </motion.button>
                    {shareUrl && (
                        <button
                            onClick={copyShare}
                            className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-aura-cyan/30 bg-white text-aura-cyan text-xs font-jetbrains-mono"
                            data-testid="share-copy-btn"
                        >
                            {shareCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                            {shareCopied ? "Copied" : "Copy link"}
                        </button>
                    )}
                    <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={exportPDF}
                        disabled={exporting}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-aura-violet/25 bg-white text-aura-text hover:bg-aura-violet/5 transition btn-ripple text-sm font-medium disabled:opacity-60"
                        data-testid="export-pdf-btn"
                    >
                        {exporting ? <div className="cyber-spinner !w-4 !h-4" /> : <ArrowDownToLine className="w-4 h-4" />}
                        Export PDF
                    </motion.button>
                    <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={() => navigate("/onboarding")}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-aura-violet to-aura-glow text-white text-sm font-medium glow-violet-soft btn-ripple"
                        data-testid="recompute-btn"
                    >
                        <Sparkles className="w-4 h-4" /> Recompute
                    </motion.button>
                </div>
            </div>

            <motion.div ref={reportRef} variants={stagger} initial="hidden" animate="show" className="space-y-6">
                {/* Hero — Score */}
                <motion.div variants={item} className="glass p-6 lg:p-10 grid lg:grid-cols-[280px_1fr] gap-8 items-center">
                    <ScoreCircle score={report.score} size={240} />
                    <div>
                        <div className="font-jetbrains-mono text-[10px] uppercase tracking-[0.3em] text-aura-violet mb-2">
                            // SYSTEM PERCEPTION
                        </div>
                        <p className="font-syne text-xl lg:text-2xl leading-snug text-aura-text" data-testid="report-perception">
                            {out.perception}
                        </p>
                        <div className="mt-5 p-4 bg-aura-elevate/80 border border-aura-violet/10 rounded-lg">
                            <div className="font-jetbrains-mono text-[10px] uppercase tracking-wider text-aura-muted mb-1">
                                Optimized identity
                            </div>
                            <div className="font-dm-sans text-sm text-aura-text">{out.optimized_identity}</div>
                        </div>
                    </div>
                </motion.div>

                {/* Subscores */}
                <motion.div variants={item} className="glass p-6 lg:p-8" data-testid="subscores-card">
                    <div className="flex items-center justify-between mb-5">
                        <h2 className="font-syne text-2xl font-semibold text-aura-text">Score Breakdown</h2>
                        <span className="font-jetbrains-mono text-[10px] tracking-wider text-aura-muted">/ 10 each</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
                        {Object.entries(SUBSCORE_LABELS).map(([k, label], i) => {
                            const v = subscores[k] ?? 0;
                            const color = colorFor(v);
                            return (
                                <div key={k} data-testid={`subscore-${k}`}>
                                    <div className="flex justify-between text-sm mb-1.5">
                                        <span className="font-dm-sans text-aura-text">{label}</span>
                                        <span className="font-jetbrains-mono font-semibold" style={{ color }}>
                                            {v.toFixed(1)}
                                        </span>
                                    </div>
                                    <div className="h-2 bg-aura-elevate rounded-full overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${(v / 10) * 100}%` }}
                                            transition={{ type: "spring", stiffness: 60, damping: 14, delay: 0.1 + i * 0.05 }}
                                            className="h-full rounded-full"
                                            style={{ background: color, boxShadow: `0 0 12px ${color}66` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </motion.div>

                {/* Strengths & Weaknesses */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <motion.div variants={item} className="glass p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <TrendingUp className="w-4 h-4 text-aura-cyan" />
                            <h3 className="font-syne text-lg font-semibold text-aura-text">Strengths</h3>
                        </div>
                        <ul className="space-y-3" data-testid="report-strengths">
                            {(out.strengths || []).map((s, i) => (
                                <li key={i} className="flex gap-3 text-sm">
                                    <span className="font-jetbrains-mono text-aura-cyan text-xs mt-0.5 font-semibold">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="text-aura-text">{s}</span>
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                    <motion.div variants={item} className="glass p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <ArrowRight className="w-4 h-4 text-orange-500 rotate-180" />
                            <h3 className="font-syne text-lg font-semibold text-aura-text">Friction Points</h3>
                        </div>
                        <ul className="space-y-3" data-testid="report-weaknesses">
                            {(out.weaknesses || []).map((s, i) => (
                                <li key={i} className="flex gap-3 text-sm">
                                    <span className="font-jetbrains-mono text-orange-500 text-xs mt-0.5 font-semibold">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="text-aura-text">{s}</span>
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                </div>

                {/* Action Plan */}
                <motion.div variants={item} className="glass p-6 lg:p-8" data-testid="action-plan">
                    <div className="flex items-center gap-2 mb-5">
                        <Calendar className="w-4 h-4 text-aura-violet" />
                        <h3 className="font-syne text-2xl font-semibold text-aura-text">7-Day Action Plan</h3>
                    </div>
                    <div className="space-y-3">
                        {(out.action_plan_7_days || []).map((d) => (
                            <motion.div
                                key={d.day}
                                whileHover={{ x: 4 }}
                                className="flex gap-4 p-4 rounded-lg bg-white/70 border border-aura-violet/10 hover:border-aura-violet/30 hover:shadow-sm transition"
                            >
                                <div className="w-12 h-12 shrink-0 rounded-lg bg-gradient-to-br from-aura-violet/15 to-aura-cyan/15 border border-aura-violet/30 flex flex-col items-center justify-center">
                                    <span className="font-jetbrains-mono text-[9px] tracking-wider text-aura-violet">DAY</span>
                                    <span className="font-syne text-lg font-bold leading-none text-aura-text">{d.day}</span>
                                </div>
                                <div className="flex-1">
                                    <div className="font-dm-sans font-medium text-aura-text">{d.task}</div>
                                    <div className="text-xs text-aura-body mt-1">{d.why}</div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Growth Strategy */}
                <motion.div variants={item} className="glass p-6 lg:p-8 border-l-4 border-aura-cyan" data-testid="growth-strategy">
                    <div className="font-jetbrains-mono text-[10px] uppercase tracking-[0.3em] text-aura-cyan mb-2">
                        // GROWTH STRATEGY
                    </div>
                    <p className="font-syne text-lg leading-relaxed text-aura-text">{out.growth_strategy}</p>
                </motion.div>
            </motion.div>
        </Layout>
    );
}

